// Shared partner / contract constants + document templates
export const PARTY1 = {
  name: "Heri Setyo",
  title: "Founder",
  contact: "+6287844715628",
  company: "Bedsolving Indonesia (PT Bumi Selaras Indonesia)",
};

export const BRAND = "Bedsolving Indonesia";

// Party 1 is editable per-contract; fall back to the company defaults
export const party1Of = (p) => ({
  name: p?.party1_name || PARTY1.name,
  title: p?.party1_title || PARTY1.title,
  contact: p?.party1_contact || PARTY1.contact,
  company: p?.party1_company || PARTY1.company,
});

export const STATUS_META = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-600" },
  admin_signed: { label: "Awaiting partner", cls: "bg-amber-100 text-amber-700" },
  partner_signed: { label: "Awaiting Bedsolving", cls: "bg-sky-100 text-sky-700" },
  completed: { label: "Completed", cls: "bg-emerald-100 text-emerald-700" },
};

export const DEFAULT_COMPENSATION = `The Partner agrees to pay 50% of the monthly system fee (BNL) as a technology smart system cost for operational purposes shared by both parties.
The Partner will compensate Bedsolving Indonesia 5% monthly from the generated revenue, calculated after deducting OTA fees, bank fees, and including an 11% tax fee.
All payments from OTAs will be made directly to the Partner's bank account.`;

function fmtLong(iso) {
  if (!iso) return null;
  const s = String(iso);
  const d = new Date(s.length === 10 ? `${s}T00:00:00` : s);
  if (isNaN(d)) return null;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

// Returns structured content for a document type: "agreement" | "authorization"
// blocks: [{ heading?, paragraphs?: string[], items?: string[], highlight? }]
export function contractSections(type, p) {
  const P1 = party1Of(p);
  const P2 = p.partner_company || p.partner_name || "________";

  if (type === "authorization") {
    return {
      title: "LETTER OF AUTHORIZATION",
      subtitle: "Surat Kuasa / Sales & Marketing Authorization",
      blocks: [
        {
          paragraphs: [
            `The undersigned, ${p.partner_name || "________"} (${p.partner_title || "Owner"}) of ${P2}, hereby grants full authorization to ${BRAND}, a Sales & Marketing Agency based in Denpasar, Bali, Indonesia, represented by ${P1.name} (${P1.title}).`,
            `This authorization empowers ${BRAND} to act on behalf of the undersigned for all sales and marketing activities across online distribution channels (OTA) for the property "${p.property_name || "________"}", located at ${p.property_address || "________"}.`,
            "The authorized scope includes, but is not limited to: creating and managing listings, setting rates and promotions, communicating with distribution channels, and honoring bookings generated on behalf of the property.",
            "This authorization remains valid for the duration of the Strategic Partnership Agreement between both parties.",
          ],
        },
      ],
    };
  }

  const effective = fmtLong(p.effective_date) || fmtLong(p.created_at) || fmtLong(new Date().toISOString().slice(0, 10));

  return {
    title: "PARTNERSHIP STRATEGY AGREEMENT",
    subtitle: `${BRAND.toUpperCase()} & ${(p.partner_company || "Partner").toUpperCase()}`,
    blocks: [
      {
        paragraphs: [
          `This Strategic Partnership Agreement ("Agreement") is entered into by and between:`,
          `${BRAND}, a Sales & Marketing Agency located in Denpasar, Bali, Indonesia,`,
          `${P2}, engaged in Property Management for Hotel & Villa businesses.`,
          "Both parties agree to form a partnership for mutual business benefit as follows:",
        ],
      },
      {
        heading: "FORMATION",
        items: [
          `${P2} appoints ${BRAND} to handle all sales and marketing activities across online distribution channels on behalf of ${P2}.`,
          `${P2} will honor every booking made by ${BRAND} as part of operational management.`,
          `${P2} will receive all generated booking details via separate email.`,
          `${P2} will process payments to ${BRAND} within a maximum of 8 days after receiving an invoice.`,
          `${BRAND} will optimize sales and marketing activities to generate positive revenue impact for both parties.`,
          `${BRAND} will share all booking data for operational purposes.`,
          `${BRAND} will provide access to the mandatory technology system for operational activities.`,
          `${BRAND} will communicate the terms and conditions of every OTA platform used.`,
          "Both parties agree to maintain good communication and keep all confidential data secure.",
        ],
      },
      {
        heading: "TERM",
        items: [
          `This Agreement is effective from ${effective}, and remains valid indefinitely unless terminated as outlined below:`,
          "Either party may terminate by providing at least 30 days' notice via email, phone, or message.",
          "Immediate termination may occur in the event of bankruptcy or other significant reasons.",
        ],
      },
      {
        heading: "CAPITAL CONTRIBUTION",
        items: [
          `The main asset contributed by ${BRAND} is ideas and strategy.`,
          `Property and business name remain the sole assets of ${P2}.`,
          "No cash or physical assets are contributed by either party.",
        ],
      },
      {
        heading: "COST AND SHARING",
        highlight: true,
        paragraphs: [p.compensation_text || DEFAULT_COMPENSATION],
      },
      {
        heading: "MANAGEMENT",
        items: [
          `${P2} retains full responsibility and authority over property management operations.`,
          `${BRAND} retains full responsibility and authority over sales and marketing activities.`,
        ],
      },
      {
        heading: "GOVERNING LAW",
        paragraphs: [
          "This Agreement is governed by and shall be interpreted in accordance with the laws of Indonesia.",
          "IN WITNESS WHEREOF, the parties have executed this Agreement on the date first written above.",
        ],
      },
    ],
  };
}
